import { Flex, Modal } from 'components';
import { P } from 'components/Typography/Typography';
import { TextField, UserSelect } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import { t } from 'i18next';
import { Dispatch, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCreateAreaMutation } from 'hooks/queries/useAreasQuery';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { IAreaFormFields } from 'types/forms/area';
import { IArea } from 'types/area';
import { mapUsersToSelectOptions } from 'utilities/userSelectOption';
import { toast } from 'react-toastify';

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<React.SetStateAction<boolean>>;
  parentArea?: Pick<IArea, 'id' | 'name'>;
}

export const AddAreaModal = ({ isModalOpen, setIsModalOpen, parentArea }: Props) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<IAreaFormFields>({
    defaultValues: { id_code: '', name: '', owner_id: undefined }
  });

  const { mutateAsync: createArea, isPending } = useCreateAreaMutation();
  const { data: users = [] } = useUsersQuery();

  const userOptions = useMemo(
    () => mapUsersToSelectOptions(users),
    [users]
  );

  const onSubmit = async (fields: IAreaFormFields) => {
    try {
      await createArea({
        area: {
          ...fields,
          parent_id: parentArea?.id ?? null
        }
      });
      toast.success(t('common.success'));
      reset();
      setIsModalOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('common.anErrorOccurred');
      toast.error(message);
    }
  };

  const handleClose = () => {
    reset();
    setIsModalOpen(false);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onCancelClick={handleClose}
      headerTitle={t(parentArea ? 'administration.Add a subarea' : 'administration.Add a parent area')}
      mainButton={{
        label: t('administration.add'),
        action: handleSubmit(onSubmit),
        isDisabled: isPending
      }}
      isSecondButtonVisible
    >
      <Flex flexDirection="column" mt={1}>
        {parentArea && (
          <P my={3} variant="body">
            {t('administration.addSubareaTo')} <strong>{parentArea.name}</strong>
          </P>
        )}

        <TextField
          {...register('id_code', { required: true })}
          label="administration.areaCode"
          error={errors.id_code ? t('validation.required') : undefined}
        />

        <TextField
          {...register('name', { required: true })}
          label="administration.name"
          error={errors.name ? t('validation.required') : undefined}
        />

        <Flex mt={2}>
          <Controller
            control={control}
            name="owner_id"
            render={({ field: { value, onChange } }) => (
              <UserSelect
                label="administration.areaOwner"
                options={userOptions}
                selectedOptions={userOptions.find((o) => o.value === value)}
                onChange={(opt) => onChange((opt as IOption)?.value)}
              />
            )}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};
